<template>
  <div
    class="event-container group/event-card relative flex flex-row rounded"
    :class="{
      'event-drag-over border-4 border-dashed': isDraggingOver,
      'border p-[3px]': !isDraggingOver,
      'border-orange-100': isDraggingOver && !droppingAllowed,
      '': !(isDraggingOver && !droppingAllowed)
    }"
    @drop="handleDroppingEventCard"
    @dragover="handleDragOverEventCard"
    @dragleave="handleDragLeaveEventCard"
  >
    <div class="flex w-1/6 flex-col items-center p-2">
      <FuneralHomeAvatar
        v-if="event.funeral_home_id"
        :id="event.funeral_home_id"
      />
      <h2
        class="font-bold"
        :class="[compactCard ? 'text-lg leading-tight' : 'text-2xl']"
      >
        {{ from_hour_formated }}
      </h2>
      <div v-if="!compactCard" class="flex flex-col gap-1">
        <div
          :data-tint="event.tint"
          class="max-w-full truncate rounded bg-primary px-2 py-0.5 text-center text-xs text-contrast-primary capitalize"
        >
          {{ formated_type }}
        </div>
        <EventCardExtraInfo
          :event="event"
          :nb-people-assigned="nbPeopleAssigned"
        />
      </div>
      <div v-else class="flex flex-row items-center gap-1">
        <div>
          <div
            class="aspect-square h-3 w-3 rounded-full bg-primary"
            :data-tint="event.tint"
          />
        </div>
        <span class="font-semibold capitalize-first">
          {{ formated_type }}
        </span>
      </div>
    </div>
    <div class="flex w-5/6 flex-col gap-1 p-2">
      <div class="flex flex-row gap-2">
        <div class="flex-1 cursor-pointer" @click="editModalOpened = true">
          <div class="flex flex-row gap-2">
            <h3
              v-if="!hasFuneralHomeFilter && event?.funeral_home_id"
              class="text-lg leading-tight font-bold capitalize-first"
            >
              {{ cFuneralHome }} -
            </h3>
            <h3 class="text-lg leading-tight font-bold capitalize-first">
              {{ cTitle }}
            </h3>
          </div>
          <div class="flex flex-row items-center gap-2">
            <div class="font-bold uppercase">
              {{ $h.rangeDateAsString(event.date_from, event.date_to) }}
            </div>
          </div>
          <div class="flex flex-row gap-2">
            <div v-if="event?.place?.model" class="flex flex-row items-center">
              <div
                v-if="event?.place?.model?.full_name"
                class="font-bold uppercase"
              >
                {{ event?.place?.model?.full_name }}
              </div>
            </div>
            <template v-if="event.type === 'TRA'">
              <div>
                <FontAwesomeIcon :icon="['fas', 'arrow-right']" />
              </div>
              <div
                v-if="event?.destination?.model"
                class="flex flex-row items-center"
              >
                <div
                  v-if="event?.destination?.model?.full_name"
                  class="font-bold uppercase"
                >
                  {{ event?.destination?.model?.full_name }}
                </div>
              </div>
            </template>
          </div>
        </div>
        <div
          v-if="compactCard && event.assignedPeople.length"
          class="flex shrink-0 flex-row"
        >
          <AvatarComponent
            v-for="assignedPerson in event.assignedPeople"
            :key="assignedPerson.id"
            :user_id="assignedPerson"
          />
        </div>
        <div class="shrink-0">
          <BaseDropDownMenu :options="options" :is-button="false" />
        </div>
      </div>
      <template v-if="!compactCard">
        <div class="flex flex-row">
          <div class="flex-1">
            <div
              class="flex flex-col font-semibold text-slate-600 dark:text-slate-200"
            >
              <div
                v-if="event?.dossier"
                class="flex min-w-0 flex-row items-center gap-1"
              >
                <span class="shrink-0 uppercase">
                  {{ $t('global.dossier') }}
                </span>
                <span class="shrink-0">:</span>
                <button
                  type="button"
                  class="min-w-0 truncate text-primary hover:underline dark:text-primary-300"
                  @click.stop="consultModalOpened = true"
                >
                  {{ event.dossier.reference }}
                </button>
              </div>
              <div
                v-if="event?.dossier?.concernedPerson"
                class="flex min-w-0 flex-row items-center gap-1"
              >
                <span class="shrink-0 uppercase">
                  {{
                    $t(
                      event.dossier.concernedPerson.is_deceased
                        ? 'global.deceased'
                        : 'global.beneficiary'
                    )
                  }}
                </span>
                <span class="shrink-0">:</span>
                <button
                  type="button"
                  class="min-w-0 truncate text-primary hover:underline dark:text-primary-300"
                  @click.stop="consultModalOpened = true"
                >
                  {{ event.dossier.concernedPerson.full_name }}
                </button>
              </div>
            </div>
            <div
              v-if="
                event?.type === 'COC' &&
                event?.properties?.coffin_closing_with_sealing_process
              "
              class="flex gap-x-2"
            >
              <span class="font-bold uppercase">
                {{ $t('attributes.coffin_closing_with_sealing_process') }}
              </span>
              <span
                v-if="event?.properties?.coffin_closing_sealing_process_time"
                class="font-medium"
              >
                {{
                  `${$t('global.at_time')} ${event?.properties?.coffin_closing_sealing_process_time}`
                }}
              </span>
              <span
                v-if="event?.properties?.coffin_closing_sealing_process_by"
                class="font-medium"
              >
                {{
                  `${$t('global.by')} ${sealingProcessOptions.find((type) => type.id === event?.properties?.coffin_closing_sealing_process_by)?.name}`
                }}
              </span>
            </div>
          </div>
          <div class="shrink-0"></div>
        </div>

        <div
          v-for="note in internalNotes"
          :key="note.key"
          class="rounded border bg-gray-50 px-1.5 py-1 text-xs font-medium whitespace-pre-line shadow-md select-none dark:bg-slate-600"
          @dblclick="editModalOpened = true"
        >
          <span
            v-if="internalNotes.length > 1"
            class="block text-[10px] font-semibold text-slate-500 capitalize-first dark:text-slate-300"
          >
            {{ $t(note.labelKey) }}
          </span>
          {{ note.content }}
        </div>

        <div class="flex flex-row items-start gap-6 pt-2 empty:hidden">
          <AssignedPeopleList
            v-model:event="localEvent"
            :editable="true"
            @fetch-events="$emit('fetch-events')"
          />
          <AssignedMaterialResourcesList
            v-model:event="localEvent"
            :editable="true"
            :can-be-dropable="materialResourceListCanBeDroppable"
            @fetch-events="$emit('fetch-events')"
          />
        </div>

        <div
          v-if="event.tasks?.length"
          class="mt-1 rounded-md border border-gray-200 px-3 py-2 dark:border-white/20"
        >
          <span
            class="mb-1.5 block text-xs font-semibold text-gray-500 capitalize-first dark:text-gray-400"
          >
            {{ $t('global.tasks') }}
          </span>
          <div class="grid grid-cols-2 gap-1.5">
            <TaskDossierCard
              v-for="(task, index) in event.tasks"
              :key="task.id ?? index"
              :task="task"
              :is-item-task="true"
              size="mini"
              @refresh-list="handleTaskCreated"
              @tasks-updated="handleTasksUpdated"
            />
          </div>
        </div>
      </template>
    </div>
    <EventModal
      v-model:open="editModalOpened"
      :selected-event-id="event.id"
      planning-mode
      force-form
      is-in-planning
      @delete-event="deleteEvent"
      @event-updated="
        () => {
          $emit('fetch-events')
          editModalOpened = false
        }
      "
    />

    <TaskDossierModal
      v-if="event.dossier_id"
      v-model:open="addTaskModalOpened"
      default-parent-type="App\Models\Event"
      :default-funeral-home-id-for-new-task-dossier="event.funeral_home_id"
      :default-parent-id="event.id"
      :default-dossier-name="event.dossier?.title"
      :locked-dossier-id="event.dossier_id"
      edition-mode
      @tasks-updated="handleTasksUpdated"
      @refresh-list="handleTaskCreated"
    />

    <ActivityLogModal
      v-if="event?.id && $can('see_activities')"
      v-model:open="activityLogModalOpened"
      :model-id="event.id"
      model-type="event"
    />

    <ConfirmModal ref="availabilityAlertModal" />
    <ConfirmModal ref="deleteEventConfirm" />

    <PlanningDossierConsultModal
      v-if="event?.dossier"
      v-model:open="consultModalOpened"
      :dossier-id="event.dossier.id"
      :dossier-reference="event.dossier.reference"
    />
  </div>
</template>

<script>
import { hasUnavailabilityDuring } from '@/utils/unavailabilityOverlap'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import AvatarComponent from '@c/AvatarComponent.vue'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import AssignedPeopleList from '@c/Dashboard/Content/Planning/components/AssignedPeopleList.vue'
import AssignedMaterialResourcesList from '@c/Dashboard/Content/Planning/components/AssignedMaterialResourcesList.vue'
import eventType from '@/assets/enums/events/EventType'
import EventModal from '@ui/modal/event/EventModal.vue'
import ActivityLogModal from '@ui/modal/activityLog/ActivityLogModal.vue'
import EventCardExtraInfo from '@c/Dashboard/Content/Planning/components/EventCardExtraInfo.vue'
import FuneralHomeAvatar from '@c/Avatar/FuneralHomeAvatar.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import TaskDossierCard from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/TaskDossierCard.vue'
import TaskDossierModal from '@ui/modal/taskDossier/TaskDossierModal.vue'
import PlanningDossierConsultModal from '@c/Dashboard/Content/Planning/components/PlanningDossierConsultModal.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'EventCard',
  components: {
    ActivityLogModal,
    FontAwesomeIcon,
    ConfirmModal,
    FuneralHomeAvatar,
    EventCardExtraInfo,
    EventModal,
    AssignedMaterialResourcesList,
    AssignedPeopleList,
    BaseDropDownMenu,
    AvatarComponent,
    TaskDossierCard,
    TaskDossierModal,
    PlanningDossierConsultModal
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    currentBoard: {
      type: Object,
      required: false,
      default: null
    },
    hasFuneralHomeFilter: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:compact-card',
    'update:event',
    'fetch-events',
    'delete-event'
  ],
  data() {
    return {
      compactCard: false,
      isDraggingOver: false,
      droppingAllowed: true,
      editModalOpened: false,
      activityLogModalOpened: false,
      consultModalOpened: false,
      addTaskModalOpened: false,
      sealingProcessOptions: [
        { id: 'FH', name: this.$t('attributes.the_funeral_home') },
        { id: 'POL', name: this.$t('attributes.the_policy') }
      ]
    }
  },
  computed: {
    ...mapGetters({
      dataTransferObject: 'planning/getDataTransferObject',
      funeralHomes: 'metadata/getFuneralHomesMetadata',
      metadata: 'metadata/getInitialMetadata'
    }),
    localEvent: {
      get() {
        return this.event
      },
      set(value) {
        this.$emit('update:event', value)
      }
    },
    isUnavailable() {
      return hasUnavailabilityDuring(
        this.dataTransferObject?.availabilityItems,
        this.localEvent.date_from,
        this.localEvent.date_to
      )
    },
    burialType() {
      if (
        this.event.type === eventType.BURIAL &&
        this.event?.properties?.burial_type
      ) {
        return this.metadata?.models?.list_items?.burial_types?.find(
          (type) => type.id === this.event.properties.burial_type
        )?.name
      }
      return ''
    },
    cTitle() {
      if (this.event?.title) return this.event.title

      if (
        this.event.type === eventType.BURIAL &&
        this.event?.properties?.burial_type
      ) {
        return (
          this.burialType +
          ' - ' +
          this.event?.dossier?.concernedPerson?.full_name
        )
      }

      return (
        this.$t(`enums.event.type.${this.event.type}`) +
        ' - ' +
        this.event?.dossier?.concernedPerson?.full_name
      )
    },
    cFuneralHome() {
      if (!this.event?.funeral_home_id) return null

      return (
        this.funeralHomes?.find((fh) => fh.id === this.event.funeral_home_id)
          ?.initials ?? null
      )
    },
    nbPeopleAssigned() {
      let people_assigned = new Set()

      if (this.event?.assignedPeopleCollection) {
        this.event.assignedPeopleCollection.forEach((person) => {
          if (person?.disponibility?.assignement_status === 'confirmed') {
            people_assigned.add(person.id)
          }
        })
      }

      if (this.event?.materialResourcesCollection) {
        this.event.materialResourcesCollection.forEach((material_resource) => {
          if (material_resource.disponibility?.assigned_people) {
            material_resource.disponibility.assigned_people.forEach((user) => {
              if (user?.disponibility?.assignement_status === 'confirmed') {
                people_assigned.add(user.id)
              }
            })
          }
        })
      }

      let usersArray = Array.from(people_assigned)

      return usersArray.length > this.event.properties?.nb_people
        ? this.event.properties?.nb_people
        : usersArray.length
    },

    eventType() {
      return eventType
    },
    from_date_formated() {
      return this.$h.formatDate(
        this.event.date_from,
        'dddd DD MMMM',
        this.$i18n.locale.substring(0, 2)
      )
    },
    from_hour_formated() {
      return this.$h.formatDate(this.event.date_from, 'HH:mm')
    },
    to_hour_formated() {
      return this.$h.formatDate(this.event.date_to, 'HH:mm')
    },
    formated_type() {
      if (
        this.event.type === eventType.BURIAL &&
        this.event?.properties?.burial_type
      ) {
        return this.burialType
      }
      return this.$t(`enums.event.type.${this.event.type}`)
    },
    options() {
      const actions = [
        {
          label: 'compact',
          icon: 'compress-alt',
          gate: true,
          action: () => this.compactCardAction()
        },
        {
          label: this.$t('button.edit'),
          icon: 'edit',
          gate: true,
          action: () => (this.editModalOpened = true)
        }
      ]
      if (this.event.id && this.$can('see_activities')) {
        actions.push({
          label: this.$t('global.activity_log'),
          icon: 'clock-rotate-left',
          gate: true,
          action: () => (this.activityLogModalOpened = true)
        })
      }
      if (this.event.dossier_id) {
        actions.push({
          label: this.$t('button.add_task'),
          icon: 'tasks',
          gate: true,
          action: () => this.openAddTaskModal()
        })
      }
      return [
        actions,
        [
          {
            label: this.$t('button.delete'),
            icon: 'trash',
            gate: true,
            bg_color: 'bg-gradient-to-b from-red-600 to-red-700',
            txt_color: 'text-white',
            action: () => this.confirmAndDeleteEvent()
          }
        ]
      ]
    },
    internalNotes() {
      return [
        {
          key: 'internal_note',
          labelKey: 'attributes.internal_note',
          content: this.event?.internal_note
        },
        {
          key: 'internal_note_planning',
          labelKey: 'attributes.internal_note_planning',
          content: this.event?.internal_note_planning
        }
      ].filter((note) => note.content?.length)
    },
    materialResourceListCanBeDroppable() {
      return this.isDraggingOver && this.dataTransferObject?.type === 'user'
    }
  },
  methods: {
    ...mapActions({
      processAddAssignedPersonAction: 'planning/addAssignedPerson',
      processCopyAssignedPeopleAction: 'planning/copyAssignedPeople',
      processCopyAssignedMaterialResourceAction:
        'planning/copyAssignedMaterialResource',
      processAddAssignedMaterialResourceAction:
        'planning/addAssignedMaterialResource',
      processRemoveAssignedMaterialResourceAction:
        'planning/removeAssignedMaterialResource',
      processRefreshEventTasksInListAction: 'agenda/refreshEventTasksInList'
    }),
    ...mapMutations({
      processSetEventTasksInListMutation: 'agenda/setEventTasksInList'
    }),
    openAddTaskModal() {
      this.$nextTick(() => {
        this.addTaskModalOpened = true
      })
    },
    handleTasksUpdated({ parent_id, tasks }) {
      this.processSetEventTasksInListMutation({ event_id: parent_id, tasks })
    },
    handleTaskCreated() {
      this.processRefreshEventTasksInListAction({ event_id: this.event.id })
    },
    compactCardAction() {
      this.compactCard = !this.compactCard
    },
    reloadEvent() {
      this.$h.toastDanger('reloadEvent not yet implemented')
    },
    deleteEvent() {
      this.$emit('delete-event', this.event.id)
    },
    async confirmAndDeleteEvent() {
      const confirmed = await this.$refs.deleteEventConfirm.show({
        title: this.$t('validation.confirm_delete'),
        okButton: this.$t('global.yes')
      })
      if (!confirmed) return

      this.deleteEvent()
    },
    checkIfUserAlreadyInEvent(user) {
      return this.event.assignedPeople.includes(user.id)
    },
    addUserToEvent(user) {
      if (this.checkIfUserAlreadyInEvent(user)) this.$h.toastSuccess('already')
      else {
        let original_assignedPeople = [...this.event.assignedPeopleCollection]
        this.event.assignedPeopleCollection.push({
          id: user.id
        })
        this.processAddAssignedPersonAction({
          event_id: this.event.id,
          user_id: user.id
        })
          .then((response) => {
            this.$h.toastSuccess(this.$t('global.added'))
            this.localEvent = response.event
            this.$emit('fetch-events')
          })
          .catch((_) => {
            this.localEvent.assignedPeopleCollection = original_assignedPeople
          })
      }
    },
    copyUsersToEvent(event_id) {
      if (this.event.id === event_id) return
      this.processCopyAssignedPeopleAction({
        event_id: this.event.id,
        from_event_id: event_id
      }).then((response) => {
        this.$h.toastSuccess(this.$t('global.added'))
        this.localEvent = response.event
        this.$emit('fetch-events')
      })
    },
    checkIfMaterialResourceAlreadyInEvent(material_resource) {
      return this.event.materialResources.includes(material_resource.id)
    },
    addMaterialResourceToEvent(material_resource) {
      if (this.checkIfMaterialResourceAlreadyInEvent(material_resource))
        this.$h.toastSuccess('already')
      else {
        let original_materialResources = [
          ...this.event.materialResourcesCollection
        ]
        this.event.materialResourcesCollection.push({
          id: material_resource.id
        })
        this.processAddAssignedMaterialResourceAction({
          event_id: this.event.id,
          material_resource_id: material_resource.id
        })
          .then((response) => {
            if (response.event) {
              this.localEvent = {
                ...response.event,
                materialResourcesCollection:
                  response.event.materialResourcesCollection ??
                  this.event.materialResourcesCollection
              }
              this.$h.toastSuccess(this.$t('global.added'))
              this.$emit('fetch-events')
            } else {
              this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
            }
          })
          .catch((_) => {
            this.localEvent.materialResourcesCollection =
              original_materialResources
          })
      }
    },
    copyMaterialResourceToEvent(dataTransferObject) {
      if (this.event.id === dataTransferObject.event) return

      this.processCopyAssignedMaterialResourceAction({
        event_id: this.event.id,
        from_event_id: dataTransferObject.event,
        material_resource: dataTransferObject.material_resource
      }).then((response) => {
        this.$h.toastSuccess(this.$t('global.added'))
        this.localEvent = response.event
        this.$emit('fetch-events')
      })
    },
    handleDroppingEventCard(event) {
      event.preventDefault()
      const dataTransferObject = { ...this.dataTransferObject }

      if (this.isUnavailable) {
        const isUser = dataTransferObject.type === 'user'
        this.$refs.availabilityAlertModal
          .show({
            title: this.$t('alert.absence_alert'),
            message: this.$t(
              `alert.absence_alert_message_${this.dataTransferObject.type}`,
              {
                name: isUser
                  ? this.dataTransferObject.model.full_name
                  : this.$trans(this.dataTransferObject.model.name),
                event: this.event.title
              }
            ),
            okButton: this.$t('button.add_anyway')
          })
          .then((response) => {
            if (!response) {
              this.handleDragLeaveEventCard()
            } else {
              this.applyHandleDroppingEventCard(dataTransferObject)
            }
          })
      } else if (
        event.target.classList.contains('resource-material-dropping-zone')
      ) {
        this.handleDragLeaveEventCard()
      } else {
        this.applyHandleDroppingEventCard(dataTransferObject)
      }
    },
    applyHandleDroppingEventCard(dataTransferObject) {
      this.isDraggingOver = false

      switch (dataTransferObject.type) {
        case 'user':
          this.addUserToEvent(dataTransferObject.model)
          break
        case 'material-resource':
          this.addMaterialResourceToEvent(dataTransferObject.model)
          break
        case 'user-list':
          this.copyUsersToEvent(dataTransferObject.event)
          break
        case 'assigned-material-resource':
          this.copyMaterialResourceToEvent(dataTransferObject)
          break
      }
    },
    handleDragOverEventCard(event) {
      this.isDraggingOver = true
      event.preventDefault()
      if (event.target.classList.contains('resource-material-dropping-zone'))
        return
      if (
        this.dataTransferObject?.type === 'user' &&
        this.checkIfUserAlreadyInEvent(this.dataTransferObject.model)
      ) {
        event.dataTransfer.dropEffect = 'none'
        this.droppingAllowed = false
      } else {
        event.dataTransfer.dropEffect = 'copy'
        this.droppingAllowed = true
      }
    },
    handleDragLeaveEventCard(event) {
      this.isDraggingOver = false
      this.droppingAllowed = true
    }
  }
}
</script>
